import {
  CreateAccountRequest,
  getAccoountNumberRequest,
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { getAccoountNumber, postAccountCreate } from "api/accountAPI";
import { useCallback, useEffect, useState } from "react";
import { AddModalPresenter, UPDATE_TYPE } from "./AddModalPresenter";
import { useMutation } from "react-query";
import * as option from "utils/options";
import { useAppSelector } from "redux/hooks";
import {
  CustomError,
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { checkOffsetTimezone } from "utils/timeUtil";
import { DISCOUNT_TIER } from "api/interfaces/subscription.interface";
import {
  defaultDiscountTier,
  getAllAccountInfo,
  handleDuplicateAccountName,
  isDiscountEmpty,
  shouldClearDiscountValidation,
} from "utils/AccountUtil";
import useStatusMutation from "hook/useStatusMutation";
import { checkEmailRegEx, checkWhiteSpaceRegEx } from "utils/regEx";
import { useAuth } from "components/Auth";
import useApiError from "hook/useApiError";
import { getAccountNameValidationError } from "../detail/AccountInner";
import { BILLING_TYPE } from "api/interfaces/planInterface.interface";
import { changeLevelFromABBToABB, checkEmpty, toTrim } from "utils/functions";

interface Props {
  onModal: () => void;
  onSuccessCreateAccount: () => void;
}

const numberPrefix = (level: LEVEL_TYPE, parentLevel: string) => {
  let createLevel = "";

  switch (level) {
    case LEVEL_TYPE.DW:
      createLevel = "C";
      break;
    case LEVEL_TYPE.CP:
      createLevel = "E";
      break;
    default:
      break;
  }

  return createLevel;
};

export function AddModalContainer(props: Props): JSX.Element {
  const auth = useAuth();
  const { handleErrorNotify, notifySuccess, notifyError, lang, intlString } =
    useApiError();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const addBaseLevel = changeLevelFromABBToABB({
    level: selectedAccount.accountLevel as LEVEL_TYPE,
    parentLevel: selectedAccount.parentLevel as LEVEL_TYPE,
  });

  // 토글 상태
  const [isToggled, setIsToggled] = useState(true);
  // 나라 선택
  const [isFlagSelected, setIsFlagSelected] = useState("US");

  const [accountNumberPrefix, setAccountNumberPrefix] = useState<string>(
    numberPrefix(selectedAccount.accountLevel, selectedAccount.parentLevel)
  );
  const [accountNumber, setAccountNumber] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);

  const [accountValidate, setAccountValidate] = useState<Validate>({
    name: false,
    isLevel: false,
    isUserName: false,
    isUserEmail: false,
    discount: false,
  });

  const [accountValidateMsg, setAccountValidateMsg] = useState<ValidateMsg>({
    name: "",
    isLevel: "",
    isUserName: "",
    isUserEmail: "",
    discount: "",
  });

  const onChangePage = (name: string, index?: number) => {
    if (name === "next") {
      if (page === 0) {
        if (onValidateCreate()) {
          return;
        }
      }
      onInitialValidate();
      setPage(page + 1);
    }
    if (name === "previous") {
      setPage(page - 1);
    }
    if (name === "save") {
      onClickCreate();
    }
    if (name === "step" && index !== undefined) {
      if (page === 0 && index === 1) {
        if (onValidateCreate()) {
          return;
        }
      }
      onInitialValidate();
      setPage(index);
    }
  };

  // 토글 onchange 함수
  const onChange = () => {
    setIsToggled(!isToggled);
  };

  // 나라 선택 함수
  const onSelectFlag = (code: string) => {
    setIsFlagSelected(code);
  };

  const accountCondition: getAccoountNumberRequest = {
    parentId: selectedAccount.accountId,
    parentLevel: selectedAccount.accountLevel,
    parentAccountNumber: selectedAccount.accountNumber,
    number: "",
  };

  useEffect(() => {
    mutationCreateAccountNumber.mutate({
      payload: accountCondition, // Account -> UpdateRequest
      accountId: selectedAccount.accountId,
    });
  }, []);

  const [accountInfo, setAccountInfo] = useState<CreateAccountRequest>({
    accountNumber: accountNumberPrefix.concat(accountNumber),
    dateFormat: option.dateFormatOptions[2].value,
    level:
      selectedAccount.accountLevel === "CP" ? LEVEL_TYPE.EU : LEVEL_TYPE.CP,
    parentLevel: selectedAccount.accountLevel,
    name: "",
    parentId: selectedAccount.accountId,
    timeFormat: option.timeFormatOptions[1].value,
    timezone:
      option.timezoneOptions.find((opt) => {
        return (
          checkOffsetTimezone(
            opt.value,
            Intl.DateTimeFormat().resolvedOptions().timeZone
          ) === opt.value
        );
      })?.value || "America/Detroit",
    accountAdditionalNumber: "",
    use2faAuth: true,
    inviteEmail: "",
    inviteName: "",
    description: "",
    discountTier: defaultDiscountTier,
    discount: 0,
    billingType: BILLING_TYPE.KEY,
  });

  const mutationCreateAccountNumber = useMutation(getAccoountNumber, {
    onSuccess: (res: any) => {
      setAccountNumber(res.result.number);
      setAccountInfo((info) => {
        return {
          ...info,
          accountNumber: accountNumberPrefix.concat(
            res.result.number as string
          ),
        };
      });
      setIsLoading(true);
    },
    onError: (e: any) => {
      setIsLoading(false);
      notifyError(lang.validateMsg.account.generateId.fail);
    },
  });

  const onChangePricing = useCallback(
    (name: string, value: DISCOUNT_TIER | number | undefined) => {
      if (shouldClearDiscountValidation(name, value)) {
        setAccountValidate((info) => {
          return { ...info, discount: false as boolean };
        });

        setAccountValidateMsg((info) => {
          return {
            ...info,
            discount: "",
          };
        });
      }

      setAccountInfo((prev: CreateAccountRequest) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    },
    [setAccountInfo]
  );

  const onChangeAccountInfo = useCallback(
    (type: UPDATE_TYPE, value: string | number | boolean | LEVEL_TYPE) => {
      if (type === UPDATE_TYPE.NAME) {
        setAccountInfo((info) => {
          return { ...info, name: value as string };
        });
      } else if (type === UPDATE_TYPE.LEVEL) {
        // level 설정 client/server 상위 선택못하게 막아야 함
        setAccountInfo((info) => {
          return { ...info, level: value as LEVEL_TYPE };
        });
        if (value === LEVEL_TYPE.EU) {
          setAccountNumberPrefix("E");
          setAccountInfo((info) => {
            return { ...info, accountNumber: "E".concat(accountNumber) };
          });
        } else {
          setAccountNumberPrefix("S");
          setAccountInfo((info) => {
            return { ...info, accountNumber: "S".concat(accountNumber) };
          });
        }
      } else if (type === UPDATE_TYPE.TIME_ZONE) {
        setAccountInfo((info) => {
          return { ...info, timezone: toTrim(value) };
        });
      } else if (type === UPDATE_TYPE.DATE_FORMAT) {
        setAccountInfo((info) => {
          return { ...info, dateFormat: toTrim(value) };
        });
      } else if (type === UPDATE_TYPE.TIME_FORMAT) {
        setAccountInfo((info) => {
          return { ...info, timeFormat: toTrim(value) };
        });
      } else if (type === UPDATE_TYPE.ACCOUNT_NUMBER) {
        setAccountInfo((info) => {
          return { ...info, accountNumber: toTrim(value) };
        });
      } else if (type === UPDATE_TYPE.INVITE_NAME) {
        setAccountInfo((info) => {
          return { ...info, inviteName: toTrim(value) };
        });
      } else if (type === UPDATE_TYPE.INVITE_EMAIL) {
        setAccountInfo((info) => {
          return { ...info, inviteEmail: toTrim(value) };
        });
      } else if (type === UPDATE_TYPE.INVITE_PHONE_NUMBER) {
        setAccountInfo((info) => {
          return { ...info, invitePhone: toTrim(value) };
        });
      } else if (type === UPDATE_TYPE.TWO_FACTOR_AUTH) {
        setAccountInfo((info) => {
          return { ...info, use2faAuth: value as boolean };
        });
      } else if (type === UPDATE_TYPE.ADDITIONAL_ACCOUNT_NUMBER) {
        setAccountInfo((info) => {
          return { ...info, accountAdditionalNumber: toTrim(value) };
        });
      } else if (type === UPDATE_TYPE.BILLING_TYPE) {
        if (addBaseLevel !== LEVEL_TYPE.CP) {
          return;
        }
        setAccountInfo((info) => {
          if (info.level !== LEVEL_TYPE.EU) return info;
          return { ...info, billingType: value as BILLING_TYPE };
        });
      }
    },
    [accountNumber, selectedAccount]
  );
  const mutationCreateAccount = useStatusMutation(postAccountCreate, {
    onSuccess: (res: any) => {
      notifySuccess(lang.validateMsg.account.create.success);
      getAllAccountInfo()
        .then((response) => {
          if (!!response.totalAccount) {
            auth.updateTotalAccount(response.totalAccount);
          }
        })
        .catch()
        .finally();
      props.onSuccessCreateAccount();
    },
    onError: (err: CustomError) => {
      if (err.response?.data.error === 3008) {
        setAccountValidate((info) => {
          return { ...info, name: true as boolean };
        });
        handleDuplicateAccountName({
          setFn: setAccountValidateMsg,
          validateKey: "name",
          elementId: "dw_account_input_name",
          message: intlString(lang.ServerErrorCode[3008]),
        });
        setPage(0);
        return;
      }
      handleErrorNotify(err, lang.validateMsg.account.create.fail);
    },
  });

  const onValidateCreate = () => {
    const name = toTrim(accountInfo.name);
    const accountNameError = getAccountNameValidationError(name);

    if (accountNameError) {
      setAccountValidate((info) => {
        return { ...info, name: true as boolean };
      });
      setAccountValidateMsg((info) => {
        return {
          ...info,
          name: intlString(lang.validateMsg.account[accountNameError]),
        };
      });
      return true;
    }

    if (checkWhiteSpaceRegEx(accountInfo.level)) {
      setAccountValidate((info) => {
        return { ...info, isLevel: true as boolean };
      });

      setAccountValidateMsg((info) => {
        return {
          ...info,
          isLevel: intlString(lang.validateMsg.account.level),
        };
      });

      return true;
    }

    if (
      checkEmpty(accountInfo.inviteEmail) &&
      accountInfo.level !== LEVEL_TYPE.EU
    ) {
      setAccountValidate((info) => {
        return { ...info, isUserEmail: true as boolean };
      });
      setAccountValidateMsg((info) => {
        return {
          ...info,
          isUserEmail: intlString(lang.validateMsg.account.userMail.empty),
        };
      });
      return true;
    }

    if (
      !checkEmpty(accountInfo.inviteEmail) &&
      !checkEmailRegEx(accountInfo.inviteEmail as string)
    ) {
      setAccountValidate((info) => {
        return { ...info, isUserEmail: true as boolean };
      });

      setAccountValidateMsg((info) => {
        return {
          ...info,
          isUserEmail: intlString(lang.validateMsg.profile.email.irregular),
        };
      });
      return true;
    }

    if (
      !checkEmpty(accountInfo.inviteEmail) &&
      toTrim(accountInfo.inviteEmail) === auth.user.email
    ) {
      setAccountValidate((info) => {
        return { ...info, isUserEmail: true as boolean };
      });
      setAccountValidateMsg((info) => {
        return {
          ...info,
          isUserEmail: intlString(lang.validateMsg.account.userMail.same),
        };
      });
      return true;
    }

    return false;
  };

  const onInitialValidate = () => {
    setAccountValidate({
      name: false,
      isLevel: false,
      isUserName: false,
      isUserEmail: false,
      discount: false,
    });
    setAccountValidateMsg({
      name: "",
      isLevel: "",
      isUserName: "",
      isUserEmail: "",
      discount: "",
    });
  };

  const onClickCreate = useCallback(() => {
    if (!mutationCreateAccount.idle) return;

    onInitialValidate();

    if (onValidateCreate()) {
      return;
    }

    if (
      selectedAccount.accountLevel === LEVEL_TYPE.DW &&
      accountInfo.discountTier === DISCOUNT_TIER.CUSTOM &&
      isDiscountEmpty(accountInfo.discount)
    ) {
      setAccountValidate((info) => {
        return { ...info, discount: true as boolean };
      });

      setAccountValidateMsg((info) => {
        return {
          ...info,
          discount: intlString(lang.validateMsg.account.discount.empty),
        };
      });
      return;
    }

    // creditLimit is not user-editable in this form yet - never send it in the create request.
    // billingType is only meaningful (and only editable) for a CP-level base account - never send it otherwise.
    const { discount, creditLimit, billingType, ...rest } = accountInfo;
    const billingTypeField =
      addBaseLevel === LEVEL_TYPE.CP ? { billingType } : {};
    const payload = (
      accountInfo.discountTier === DISCOUNT_TIER.CUSTOM
        ? {
            ...rest,
            ...billingTypeField,
            discount,
            name: toTrim(accountInfo.name),
            accountAdditionalNumber: toTrim(
              accountInfo.accountAdditionalNumber || ""
            ),
            description: toTrim(accountInfo.description),
            inviteEmail: toTrim(accountInfo.inviteEmail),
          }
        : {
            ...rest,
            ...billingTypeField,
            name: toTrim(accountInfo.name),
            accountAdditionalNumber: toTrim(
              accountInfo.accountAdditionalNumber || ""
            ),
            description: toTrim(accountInfo.description),
            inviteEmail: toTrim(accountInfo.inviteEmail),
          }
    ) as CreateAccountRequest;

    mutationCreateAccount.mutate({
      payload, // Account -> UpdateRequest
      accountId: selectedAccount.accountId,
    });
  }, [mutationCreateAccount, selectedAccount.accountId, accountInfo, addBaseLevel]);

  return (
    <>
      {isLoading && (
        <AddModalPresenter
          accountInfo={accountInfo}
          onClickCreate={onClickCreate}
          onChageAccountInfo={onChangeAccountInfo}
          accountNumberPrefix={accountNumberPrefix}
          accountNumber={accountNumber}
          isToggled={isToggled}
          onChange={onChange}
          onModal={props.onModal}
          isFlagSelected={isFlagSelected}
          onSelectFlag={onSelectFlag}
          accountValidate={accountValidate as Validate}
          accountValidateMsg={accountValidateMsg as ValidateMsg}
          parentLevel={selectedAccount.parentLevel as LEVEL_TYPE}
          currentLevel={selectedAccount.accountLevel}
          isCreationLoading={mutationCreateAccount.isCreateLoading}
          isCreateDisabled={mutationCreateAccount.isCreateDisabled}
          onChangePage={onChangePage}
          page={page}
          onChangePricing={onChangePricing}
          onValidateCreate={onValidateCreate}
          setAccountValidateMsg={setAccountValidateMsg}
        />
      )}
    </>
  );
}
